import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useNavigate } from 'react-router'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  BOOKING_SESSION_STATUS_MAP,
  EVENT_TYPES_ARRAY,
} from 'src/consumer/constants'
import {
  getVcAuditSessionDetails,
  listVcAuditSessions,
} from './vcAuditSessionsService'

dayjs.extend(utc)

const STATUS_FILTER_OPTIONS = [
  { id: 'all', label: 'All Sessions' },
  { id: 'live', label: 'Live Only' },
  { id: 'completed', label: 'Completed Only' },
]

const PAGE_SIZE_OPTIONS = [10, 20, 50]

function formatDateTime(value) {
  if (!value) {
    return 'NA'
  }
  return dayjs(value).format('DD MMM YYYY, hh:mm A')
}

function toIsoStart(dateValue) {
  if (!dateValue) {
    return undefined
  }
  return dayjs(dateValue).startOf('day').toISOString()
}

function toIsoEnd(dateValue) {
  if (!dateValue) {
    return undefined
  }
  return dayjs(dateValue).endOf('day').toISOString()
}

function getEventTypeLabel(eventType) {
  return EVENT_TYPES_ARRAY.find((option) => option?.id === eventType)?.title || eventType || 'Unknown Event'
}

function getSessionStatusLabel(sessionStatus) {
  return BOOKING_SESSION_STATUS_MAP?.[sessionStatus]?.title || sessionStatus || 'Unknown'
}

function getSessionStatusChipColor(sessionStatus) {
  switch (sessionStatus) {
    case 'in-progress':
      return 'error'
    case 'marked-completed':
    case 'finished':
      return 'success'
    default:
      return 'default'
  }
}

function toOptionMap(items, getId, getLabel) {
  const seen = new Map()
  items.forEach((item) => {
    const id = getId(item)
    if (!id || seen.has(id)) {
      return
    }
    seen.set(id, {
      id,
      label: getLabel(item) || id,
    })
  })
  return Array.from(seen.values())
}

function mergeOptionLists(currentOptions, incomingOptions) {
  return toOptionMap(
    [...(currentOptions || []), ...(incomingOptions || [])],
    (option) => option?.id,
    (option) => option?.label
  )
}

function SessionDetailDialog({
  open,
  session,
  loading,
  error,
  onClose,
}) {
  const bookedEvent = session?.bookedEvent || {}
  const clinic = session?.clinic || {}
  const mentor = session?.mentor || {}
  const patient = session?.patient || {}
  const latestRecording = session?.latestRecording || {}

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Session Details</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : session ? (
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">{bookedEvent.eventName || getEventTypeLabel(bookedEvent.eventType)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {getEventTypeLabel(bookedEvent.eventType)} • {getSessionStatusLabel(bookedEvent.sessionStatus)}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Schedule
              </Typography>
              <Typography variant="body2">Start: {formatDateTime(bookedEvent.startTime)}</Typography>
              <Typography variant="body2">End: {formatDateTime(bookedEvent.endTime)}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Clinic
              </Typography>
              <Typography variant="body2">
                {clinic.clinicBrandName || 'Unknown Clinic'}
                {clinic.clinicLocationName ? ` • ${clinic.clinicLocationName}` : ''}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Participants
              </Typography>
              <Typography variant="body2">Practitioner: {mentor.name || 'Unknown'}</Typography>
              <Typography variant="body2">Patient: {patient.name || patient.patientNumber || 'Unknown'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Access
              </Typography>
              <Typography variant="body2">Live viewer: {session.canViewLive ? 'Available' : 'Unavailable'}</Typography>
              <Typography variant="body2">
                Recordings: {session.canViewRecordings ? 'Available' : 'Unavailable'}
              </Typography>
              <Typography variant="body2">
                Recording count: {session.recordingCount || 0}
              </Typography>
            </Box>
            {latestRecording?.id && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Latest Recording
                </Typography>
                <Typography variant="body2">State: {latestRecording.recordingState || 'Unknown'}</Typography>
                <Typography variant="body2">Provider: {latestRecording.recordingProvider || 'Unknown'}</Typography>
                <Typography variant="body2">
                  Started: {formatDateTime(latestRecording.recordingStartedAt)}
                </Typography>
                <Typography variant="body2">
                  Stopped: {formatDateTime(latestRecording.recordingStoppedAt)}
                </Typography>
              </Box>
            )}
          </Stack>
        ) : (
          <Alert severity="info">No session details found.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default function VcAuditBookedEventsView({ showHeader = true }) {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [filters, setFilters] = useState({
    clinicBrandId: '',
    clinicLocationId: '',
    eventType: '',
    dateFrom: '',
    dateTo: '',
    sessionView: 'all',
    recordingAvailableOnly: false,
  })
  const [sessionsPage, setSessionsPage] = useState({
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: rowsPerPage,
    number: page,
  })
  const [liveCount, setLiveCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailSession, setDetailSession] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [catalog, setCatalog] = useState({
    clinicBrands: [],
    clinicLocations: [],
  })

  useEffect(() => {
    let active = true

    async function loadSessions() {
      setLoading(true)
      setError('')

      const sessionStatusIds =
        filters.sessionView === 'live'
          ? ['in-progress']
          : filters.sessionView === 'completed'
            ? ['marked-completed', 'finished']
            : undefined

      const result = await listVcAuditSessions({
        page,
        size: rowsPerPage,
        clinicBrandId: filters.clinicBrandId || undefined,
        clinicLocationId: filters.clinicLocationId || undefined,
        eventType: filters.eventType || undefined,
        sessionStatusIds,
        startTime: toIsoStart(filters.dateFrom),
        endTime: toIsoEnd(filters.dateTo),
      })

      if (!active) {
        return
      }

      if (!result.ok) {
        setSessionsPage({
          content: [],
          totalElements: 0,
          totalPages: 0,
          size: rowsPerPage,
          number: page,
        })
        setLiveCount(0)
        setError(result.message || 'Unable to load sessions.')
        setLoading(false)
        return
      }

      const nextPage = result.sessionsPage || {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: rowsPerPage,
        number: page,
      }

      setSessionsPage(nextPage)
      setLiveCount(result.liveCount || 0)

      if (Array.isArray(nextPage.content) && nextPage.content.length > 0) {
        const nextBrandOptions = toOptionMap(
          nextPage.content,
          (session) => session?.clinic?.clinicBrandId,
          (session) => session?.clinic?.clinicBrandName
        )
        const nextLocationOptions = toOptionMap(
          nextPage.content,
          (session) => session?.clinic?.clinicLocationId,
          (session) => session?.clinic?.clinicLocationName
        )

        setCatalog((prevCatalog) => ({
          clinicBrands: mergeOptionLists(prevCatalog.clinicBrands, nextBrandOptions),
          clinicLocations: mergeOptionLists(prevCatalog.clinicLocations, nextLocationOptions),
        }))
      }

      setLoading(false)
    }

    loadSessions()

    return () => {
      active = false
    }
  }, [filters, page, rowsPerPage])

  const visibleSessions = useMemo(() => {
    const baseContent = Array.isArray(sessionsPage?.content) ? sessionsPage.content : []
    if (!filters.recordingAvailableOnly) {
      return baseContent
    }
    return baseContent.filter(
      (session) => session?.canViewRecordings || session?.recordingCount > 0 || session?.latestRecording?.id
    )
  }, [filters.recordingAvailableOnly, sessionsPage])

  const clinicBrandOptions = useMemo(() => {
    return catalog.clinicBrands.sort((left, right) => left.label.localeCompare(right.label))
  }, [catalog.clinicBrands])

  const clinicLocationOptions = useMemo(() => {
    return catalog.clinicLocations.sort((left, right) => left.label.localeCompare(right.label))
  }, [catalog.clinicLocations])

  const resultCount = filters.recordingAvailableOnly
    ? visibleSessions.length
    : sessionsPage?.totalElements || 0

  const handleFilterChange = (field, nextValue) => {
    setPage(0)
    setFilters((prevFilters) => {
      const nextFilters = {
        ...prevFilters,
        [field]: nextValue,
      }
      if (field === 'clinicBrandId') {
        nextFilters.clinicLocationId = ''
      }
      return nextFilters
    })
  }

  const handleOpenDetails = async (sessionSummary) => {
    setDetailOpen(true)
    setDetailSession(sessionSummary)
    setDetailLoading(true)
    setDetailError('')

    const result = await getVcAuditSessionDetails(sessionSummary?.bookedEvent?.id)
    if (!result.ok) {
      setDetailError(result.message || 'Unable to load session details.')
      setDetailLoading(false)
      return
    }

    setDetailSession(result.session || sessionSummary)
    setDetailLoading(false)
  }

  const handleOpenRecordingPlayback = (sessionSummary) => {
    const bookedEventId = sessionSummary?.bookedEvent?.id
    if (!bookedEventId) {
      return
    }

    const latestRecordingId = sessionSummary?.latestRecording?.id
    const nextPath = latestRecordingId
      ? `/support-home/booked-events/${bookedEventId}/recordings/${latestRecordingId}`
      : `/support-home/booked-events/${bookedEventId}/recordings`

    navigate(nextPath, {
      state: {
        from: '/support-home/booked-events',
      },
    })
  }

  return (
    <>
      {showHeader ? <SupportHeader heading="Bookings & Appointments" /> : null}
      <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Alert severity="info">
          VC audit access is scoped by your profile. Use this screen to discover allowed sessions, watch live events,
          and open stored recordings.
        </Alert>

        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6">VC Audit Sessions</Typography>
                <Typography variant="body2" color="text.secondary">
                  Filter the consultations and therapy sessions allowed by your VC audit profile.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  icon={<FiberManualRecordIcon sx={{ color: '#dc2626 !important' }} />}
                  label={`Live now: ${liveCount}`}
                  color={liveCount > 0 ? 'error' : 'default'}
                  variant={liveCount > 0 ? 'filled' : 'outlined'}
                />
                <Chip label={`Results: ${resultCount}`} variant="outlined" />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              <FormControl size="small" fullWidth>
                <InputLabel id="vc-audit-clinic-brand-label">Clinic</InputLabel>
                <Select
                  labelId="vc-audit-clinic-brand-label"
                  label="Clinic"
                  value={filters.clinicBrandId}
                  onChange={(event) => handleFilterChange('clinicBrandId', event.target.value)}
                >
                  <MenuItem value="">All Clinics</MenuItem>
                  {clinicBrandOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="vc-audit-clinic-location-label">Location</InputLabel>
                <Select
                  labelId="vc-audit-clinic-location-label"
                  label="Location"
                  value={filters.clinicLocationId}
                  onChange={(event) => handleFilterChange('clinicLocationId', event.target.value)}
                >
                  <MenuItem value="">All Locations</MenuItem>
                  {clinicLocationOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="vc-audit-event-type-label">Event Type</InputLabel>
                <Select
                  labelId="vc-audit-event-type-label"
                  label="Event Type"
                  value={filters.eventType}
                  onChange={(event) => handleFilterChange('eventType', event.target.value)}
                >
                  <MenuItem value="">All Event Types</MenuItem>
                  {EVENT_TYPES_ARRAY.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="vc-audit-session-view-label">Session View</InputLabel>
                <Select
                  labelId="vc-audit-session-view-label"
                  label="Session View"
                  value={filters.sessionView}
                  onChange={(event) => handleFilterChange('sessionView', event.target.value)}
                >
                  {STATUS_FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="From"
                type="date"
                value={filters.dateFrom}
                onChange={(event) => handleFilterChange('dateFrom', event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <TextField
                size="small"
                label="To"
                type="date"
                value={filters.dateTo}
                onChange={(event) => handleFilterChange('dateTo', event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <Stack direction="row" alignItems="center" spacing={2} sx={{ gridColumn: { xs: '1 / -1', lg: 'span 2' } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.recordingAvailableOnly}
                      onChange={(event) => handleFilterChange('recordingAvailableOnly', event.target.checked)}
                    />
                  }
                  label="Recording available"
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setPage(0)
                    setFilters({
                      clinicBrandId: '',
                      clinicLocationId: '',
                      eventType: '',
                      dateFrom: '',
                      dateTo: '',
                      sessionView: 'all',
                      recordingAvailableOnly: false,
                    })
                  }}
                >
                  Reset Filters
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : visibleSessions.length === 0 ? (
          <Alert severity="warning">
            No booked events matched the current VC audit filters.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {visibleSessions.map((session) => (
              <Card key={session?.bookedEvent?.id} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', md: 'flex-start' }}
                      spacing={1.5}
                    >
                      <Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                          <Chip size="small" label={getEventTypeLabel(session?.bookedEvent?.eventType)} />
                          <Chip
                            size="small"
                            label={getSessionStatusLabel(session?.bookedEvent?.sessionStatus)}
                            color={getSessionStatusChipColor(session?.bookedEvent?.sessionStatus)}
                            variant="outlined"
                          />
                          {session?.isLive ? (
                            <Chip
                              size="small"
                              color="error"
                              icon={<FiberManualRecordIcon />}
                              label={session?.liveBadgeLabel || 'Live'}
                            />
                          ) : null}
                          {session?.recordingCount > 0 ? (
                            <Chip
                              size="small"
                              color="secondary"
                              variant="outlined"
                              label={`${session.recordingCount} recording${session.recordingCount > 1 ? 's' : ''}`}
                            />
                          ) : null}
                        </Stack>
                        <Typography variant="h6" sx={{ mb: 0.5 }}>
                          {session?.bookedEvent?.eventName || getEventTypeLabel(session?.bookedEvent?.eventType)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(session?.bookedEvent?.startTime)}
                          {session?.bookedEvent?.endTime ? ` to ${formatDateTime(session?.bookedEvent?.endTime)}` : ''}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          variant="contained"
                          startIcon={<PlayCircleOutlineIcon />}
                          disabled={!session?.canViewLive || !session?.isLive}
                          onClick={() =>
                            navigate(
                              `/support-home/booked-events/${session?.bookedEvent?.id}/live`,
                              {
                                state: {
                                  from: '/support-home/booked-events',
                                },
                              },
                            )
                          }
                        >
                          Play Live
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<VideoLibraryOutlinedIcon />}
                          disabled={!session?.canViewRecordings}
                          onClick={() => handleOpenRecordingPlayback(session)}
                        >
                          Play Recording
                        </Button>
                        <Button
                          variant="text"
                          startIcon={<VisibilityOutlinedIcon />}
                          onClick={() => handleOpenDetails(session)}
                        >
                          View Details
                        </Button>
                      </Stack>
                    </Stack>

                    <Divider />

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Clinic
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {session?.clinic?.clinicBrandName || 'Unknown Clinic'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {session?.clinic?.clinicLocationName || 'Unknown Location'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Practitioner
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {session?.mentor?.name || 'Unknown Practitioner'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Patient
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {session?.patient?.name || session?.patient?.patientNumber || 'Unknown Patient'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {session?.patient?.mobile || session?.patient?.email || 'No contact details'}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}

            <TablePagination
              component="div"
              count={resultCount}
              page={page}
              onPageChange={(event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                const nextRowsPerPage = Number(event.target.value)
                setRowsPerPage(nextRowsPerPage)
                setPage(0)
              }}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            />
          </Stack>
        )}
      </Box>

      <SessionDetailDialog
        open={detailOpen}
        session={detailSession}
        loading={detailLoading}
        error={detailError}
        onClose={() => {
          setDetailOpen(false)
          setDetailSession(null)
          setDetailError('')
        }}
      />
    </>
  )
}
